import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../api/queryKeys'
import { ApiRequestError } from '../lib/httpClient'
import {
  deleteNotificationOption,
  deleteReportBuilder,
  deleteReportBuilderList,
  deleteReportBuilderSchedule,
  fetchNotificationList,
  fetchNotificationOptionOrganisations,
  fetchNotificationOptionsList,
  fetchRecipients,
  fetchReportBuilder,
  fetchReportBuilderHomeList,
  fetchReportBuilderDownload,
  fetchReportBuilderList,
  fetchReportBuilderOptions,
  fetchReportBuilderPreviewData,
  fetchReportBuilderSchedule,
  fetchReportBuilderScheduleList,
  fetchReports,
  bulkCreateReportBuilderSchedule,
  getAvailablePlatformTypes,
  saveNotificationList,
  saveNotificationOptionsList,
  saveReportBuilder,
  saveReportBuilderGroup,
  saveReportBuilderOptions,
  saveReportBuilderSchedule,
  startReportBuilderDownload,
  startReportBuilderPreview,
  updateNotificationSample,
} from '../api/reports'
import { useAuthSession } from '../lib/session'
import type {
  NotificationListUpsertRequest,
  NotificationOptionsUpsertRequest,
  ReportBuilderGroupUpsertRequest,
  ReportBuilderDownload,
  ReportBuilderOptionsUpsertRequest,
  ReportBuilderPreviewResult,
  ReportBuilderPreviewRequest,
  ReportBuilderScheduleBulkCreateRequest,
  ReportBuilderScheduleUpsertRequest,
  ReportBuilderUpsertRequest,
} from '../types/reports'

function invalidateReports(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.reports.all })
}

export function useReports(filters: { title?: string; status?: string } = {}) {
  const session = useAuthSession()
  const clientId = session?.currentClientId || ''

  return useQuery({
    queryKey: queryKeys.reports.list({ ...filters, clientId }),
    queryFn: () => fetchReports({ ...filters, clientId }),
    enabled: !!clientId,
  })
}

export function useReportBuilderList(clientId: string, isTemplate: boolean, filters: { title?: string } = {}) {
  return useQuery({
    queryKey: queryKeys.reports.reportBuilderList(clientId, isTemplate, filters),
    queryFn: () => fetchReportBuilderList({ clientId, isTemplate, ...filters }),
    enabled: !!clientId,
  })
}

export function useReportBuilderHomeList(clientId: string, isTemplate: boolean) {
  return useQuery({
    queryKey: queryKeys.reports.reportBuilderHomeList(clientId, isTemplate),
    queryFn: () => fetchReportBuilderHomeList({ clientId, isTemplate }),
    enabled: !!clientId,
  })
}

export function useReportBuilder(reportBuilderId: string) {
  return useQuery({
    queryKey: queryKeys.reports.reportBuilder(reportBuilderId),
    queryFn: () => fetchReportBuilder(reportBuilderId),
    enabled: !!reportBuilderId,
  })
}

export function useReportBuilderOptions(clientId: string, platformType: number) {
  return useQuery({
    queryKey: queryKeys.reports.reportBuilderOptions(clientId, platformType),
    queryFn: () => fetchReportBuilderOptions({ clientId, platformType }),
    enabled: !!clientId && !!platformType,
  })
}

export function useAvailableReportPlatformTypes(clientId: string) {
  return useQuery({
    queryKey: ['reports', 'platformTypes', clientId] as const,
    queryFn: () => getAvailablePlatformTypes(clientId),
    enabled: !!clientId,
  })
}

export function useReportBuilderScheduleList(clientId: string, id: string, isGroup = false) {
  return useQuery({
    queryKey: queryKeys.reports.reportBuilderScheduleList(clientId, id, isGroup),
    queryFn: () => fetchReportBuilderScheduleList({ clientId, id, isGroup }),
    enabled: !!clientId && !!id,
  })
}

export function useReportBuilderSchedule(reportBuilderScheduleId: string) {
  return useQuery({
    queryKey: queryKeys.reports.reportBuilderSchedule(reportBuilderScheduleId),
    queryFn: () => fetchReportBuilderSchedule(reportBuilderScheduleId),
    enabled: !!reportBuilderScheduleId,
  })
}

export function useRecipients(clientId: string) {
  return useQuery({
    queryKey: queryKeys.reports.recipients(clientId),
    queryFn: () => fetchRecipients(clientId),
    enabled: !!clientId,
  })
}

export function useNotificationOptionsList(clientId?: string) {
  return useQuery({
    queryKey: queryKeys.reports.notificationOptionsList(clientId),
    queryFn: () => fetchNotificationOptionsList(clientId),
  })
}

export function useNotificationList(clientId: string, notificationOptionID: string) {
  return useQuery({
    queryKey: queryKeys.reports.notificationList(clientId, notificationOptionID),
    queryFn: () => fetchNotificationList({ clientId, notificationOptionID }),
    enabled: !!clientId && !!notificationOptionID,
  })
}

export function useNotificationOptionOrganisations(clientId: string, platformType: number) {
  return useQuery({
    queryKey: queryKeys.reports.notificationOptionOrganisations(clientId, platformType),
    queryFn: () => fetchNotificationOptionOrganisations({ clientId, platformType }),
    enabled: !!clientId && !!platformType,
  })
}

export function useReportBuilderPreviewData(clientId: string, reportBuilderFileId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.reports.previewData(clientId, reportBuilderFileId),
    queryFn: () => fetchReportBuilderPreviewData({ clientId, reportBuilderFileId }),
    enabled: enabled && !!clientId && !!reportBuilderFileId,
  })
}

export function useReportBuilderDownloadStatus(clientId: string, reportBuilderFileId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.reports.downloadStatus(clientId, reportBuilderFileId),
    queryFn: () => fetchReportBuilderDownload({ clientId, reportBuilderFileId }),
    enabled: enabled && !!clientId && !!reportBuilderFileId,
  })
}

export function useSaveReportBuilderMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ReportBuilderUpsertRequest) => saveReportBuilder(payload),
    onSuccess: () => invalidateReports(queryClient),
  })
}

export function useDeleteReportBuilderMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reportBuilderId: string) => deleteReportBuilder(reportBuilderId),
    onSuccess: () => invalidateReports(queryClient),
  })
}

export function useDeleteReportBuilderListMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reportBuilderIdList: string[]) => deleteReportBuilderList(reportBuilderIdList),
    onSuccess: () => invalidateReports(queryClient),
  })
}

export function useSaveReportBuilderScheduleMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ReportBuilderScheduleUpsertRequest) => saveReportBuilderSchedule(payload),
    onSuccess: () => invalidateReports(queryClient),
  })
}

export function useBulkCreateReportBuilderScheduleMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ReportBuilderScheduleBulkCreateRequest) => bulkCreateReportBuilderSchedule(payload),
    onSuccess: () => invalidateReports(queryClient),
  })
}

export function useDeleteReportBuilderScheduleMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reportBuilderScheduleId: string) => deleteReportBuilderSchedule(reportBuilderScheduleId),
    onSuccess: () => invalidateReports(queryClient),
  })
}

export function useSaveReportBuilderOptionsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ReportBuilderOptionsUpsertRequest) => saveReportBuilderOptions(payload),
    onSuccess: () => invalidateReports(queryClient),
  })
}

export function useSaveNotificationOptionsListMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: NotificationOptionsUpsertRequest) => saveNotificationOptionsList(payload),
    onSuccess: () => invalidateReports(queryClient),
  })
}

export function useSaveNotificationListMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: NotificationListUpsertRequest) => saveNotificationList(payload),
    onSuccess: () => invalidateReports(queryClient),
  })
}

export function useSaveReportBuilderGroupMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ReportBuilderGroupUpsertRequest) => saveReportBuilderGroup(payload),
    onSuccess: () => invalidateReports(queryClient),
  })
}

export function useDeleteNotificationOptionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (notificationOptionID: string) => deleteNotificationOption(notificationOptionID),
    onSuccess: () => invalidateReports(queryClient),
  })
}

export function useStartReportBuilderPreviewMutation() {
  return useMutation({
    mutationFn: (payload: ReportBuilderPreviewRequest) => startReportBuilderPreview(payload),
  })
}

export function useStartReportBuilderDownloadMutation() {
  return useMutation({
    mutationFn: (payload: ReportBuilderDownload) => startReportBuilderDownload(payload),
  })
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function tryParseJsonString(value: unknown): unknown {
  if (typeof value !== 'string') return value
  const trimmed = value.trim()
  if (!trimmed) return value
  if ((!trimmed.startsWith('{') || !trimmed.endsWith('}')) && (!trimmed.startsWith('[') || !trimmed.endsWith(']'))) {
    return value
  }
  try {
    return JSON.parse(trimmed)
  } catch {
    return value
  }
}

function asPreviewRows(value: unknown): Record<string, unknown>[] | null {
  const parsed = tryParseJsonString(value)
  if (Array.isArray(parsed)) {
    if (parsed.every((item) => item && typeof item === 'object' && !Array.isArray(item))) {
      return parsed as Record<string, unknown>[]
    }
    return null
  }
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    return [parsed as Record<string, unknown>]
  }
  return null
}

function extractPreviewRows(response: unknown): Record<string, unknown>[] | null {
  const root = tryParseJsonString(response)
  const candidates = [
    (root as any)?.data?.logicAppResponse,
    (root as any)?.Data?.logicAppResponse,
    (root as any)?.logicAppResponse,
    (root as any)?.LogicAppResponse,
    (root as any)?.data?.result,
    (root as any)?.Data?.Result,
    (root as any)?.data,
    (root as any)?.Data,
    (root as any)?.result,
    (root as any)?.Result,
    (root as any)?.payload,
    (root as any)?.Payload,
    root,
  ]

  for (const candidate of candidates) {
    const rows = asPreviewRows(candidate)
    if (rows) return rows
  }

  return null
}

function extractResponseId(response: unknown): string | null {
  const root = tryParseJsonString(response)
  const candidates = [
    root,
    (root as any)?.data,
    (root as any)?.Data,
    (root as any)?.result,
    (root as any)?.Result,
    (root as any)?.reportBuilderFileId,
    (root as any)?.ReportBuilderFileId,
  ]

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim()
    }
    if (candidate && typeof candidate === 'object') {
      const nested = [
        (candidate as any).reportBuilderFileId,
        (candidate as any).ReportBuilderFileId,
        (candidate as any).id,
        (candidate as any).Id,
        (candidate as any).value,
        (candidate as any).Value,
      ]
      for (const value of nested) {
        if (typeof value === 'string' && value.trim()) {
          return value.trim()
        }
      }
    }
  }

  return null
}

function extractDownloadStatus(response: unknown): { presignedUrl?: string; fileName?: string; pending: boolean } {
  const root = tryParseJsonString(response)
  const pendingStatus = String((root as any)?.status ?? (root as any)?.Status ?? '').toLowerCase()
  const pendingMessage = String((root as any)?.message ?? (root as any)?.Message ?? '').toLowerCase()

  const candidates = [
    root,
    (root as any)?.data,
    (root as any)?.Data,
    (root as any)?.result,
    (root as any)?.Result,
    (root as any)?.payload,
    (root as any)?.Payload,
  ]

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object') continue
    const presignedUrl = (candidate as any).presignedUrl ?? (candidate as any).PresignedUrl
    const fileName = (candidate as any).fileName ?? (candidate as any).FileName
    if (typeof presignedUrl === 'string' && presignedUrl.trim()) {
      return {
        presignedUrl: presignedUrl.trim(),
        fileName: typeof fileName === 'string' && fileName.trim() ? fileName.trim() : undefined,
        pending: false,
      }
    }
  }

  return {
    pending: pendingStatus === 'pending' || pendingMessage.includes('being generated') || pendingMessage.includes('generating'),
  }
}

function getApiErrorStatus(error: unknown) {
  if (error instanceof ApiRequestError) {
    return error.status
  }

  if (error && typeof error === 'object' && 'status' in error) {
    const status = Number((error as { status: unknown }).status)
    return Number.isFinite(status) ? status : undefined
  }

  return undefined
}

export function useGenerateReportBuilderPreviewMutation() {
  return useMutation({
    mutationFn: async (payload: ReportBuilderPreviewRequest): Promise<ReportBuilderPreviewResult> => {
      const previewFileId = await startReportBuilderPreview(payload)
      if (!previewFileId) {
        throw new Error('Failed to initiate preview.')
      }

      const intervalMs = 10000
      const maxTries = 100
      let sawPending = false
      for (let i = 0; i < maxTries; i += 1) {
        let shouldContinuePolling = false
        try {
          const response = await fetchReportBuilderPreviewData({ clientId: payload.clientId, reportBuilderFileId: previewFileId })
          const rootStatus = String(response?.status ?? response?.Status ?? '').toLowerCase()
          const rootMessage = String(response?.message ?? response?.Message ?? '').toLowerCase()

          if (rootStatus === 'pending' || rootMessage.includes('being generated')) {
            sawPending = true
            shouldContinuePolling = true
          } else {
            const previewRows = extractPreviewRows(response)
            if (previewRows) {
              return {
                reportBuilderFileId: previewFileId,
                rows: previewRows,
              }
            }
            if (import.meta.env.DEV) {
              console.warn('Unmatched preview response shape', response)
            }
            shouldContinuePolling = true
          }
        } catch (error) {
          if (getApiErrorStatus(error) === 404) {
            throw new Error('No data found for the report. Please try again with a different report configuration.');
          } else {
            throw error
          }
        }

        if (shouldContinuePolling) {
          if (i < maxTries - 1) {
            await wait(intervalMs)
            continue
          }
          break
        }
      }

      if (sawPending) {
        throw new Error('Preview is still being generated. Please try again.')
      }
      throw new Error('Failed to generate preview.')
    },
  })
}

export function useGenerateReportBuilderDownloadMutation() {
  return useMutation({
    mutationFn: async (payload: ReportBuilderDownload) => {
      const startResponse = await startReportBuilderDownload(payload)
      const fileId = extractResponseId(startResponse)
      if (!fileId) {
        throw new Error('Failed to initiate export.')
      }

      const intervalMs = 10000
      const maxTries = 100
      let fileName = `${payload.reportTitle}.${payload.fileType || 'csv'}`
      let sawNotFound = false
      let sawPending = false
      for (let i = 0; i < maxTries; i += 1) {
        let shouldContinuePolling = false
        try {
          const response = await fetchReportBuilderDownload({ clientId: payload.clientId, reportBuilderFileId: fileId })
          const status = extractDownloadStatus(response)
          if (status.presignedUrl) {
            fileName = status.fileName || fileName
            return { presignedUrl: status.presignedUrl, fileName }
          }
          sawPending = sawPending || status.pending
          shouldContinuePolling = true
        } catch (error) {
          if (error instanceof ApiRequestError && error.status === 404) {
            sawNotFound = true
            shouldContinuePolling = true
          } else {
            throw error
          }
        }

        if (shouldContinuePolling && i < maxTries - 1) {
          await wait(intervalMs)
        }
      }

      if (sawNotFound || sawPending) {
        throw new Error('Export timed out before file was ready.')
      }
      throw new Error('Failed to export report.')
    },
  })
}

export function useUpdateNotificationSampleMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { notificationListID: string; clientId: string }) => updateNotificationSample(payload),
    onSuccess: () => invalidateReports(queryClient),
  })
}
